import React from 'react';
import { View } from 'react-native';
import moment from 'moment';
import { PathCategoryId, PoiCategoryId, AoiCategoryId } from '@sodraberget/shared';
import { hasIcon } from '@sodraberget/map-content/src/icon-library/iconLibrary.generated';
import { LibraryIcon } from '../components/library-icon';
import { colors } from './variables';
import { DisplayStatus, InformationSeverity, PathDifficulty, PathStatus } from '../enums';
import { 
    AoiImportant,
    AoiInfo,
    AoiWarning,
    DifficultyVeryEasy, 
    DifficultyEasy, 
    DifficultyMedium, 
    DifficultyHard, 
    PathCrossCountrySkiing, 
    PathSnowmobiling, 
    PathMountainBiking, 
    PathHiking, 
    PathRunning,
    PathRiding,
    PathCanoe,
    PathChairLift,
    PathGondola,
    PathSurfaceLift,
    PathBeltLift,
    PathSkiSlope,
    PoiRestaurants, 
    PoiAttractions, 
    PoiViewpoints, 
    PoiParking,
    PoiBusStops,
    PoiCabins,
    PoiShelters,
    PoiStores,
    PoiToilets,
    PoiWarnings,  
    PoiCafes, 
    PoiPlaceOfInterests,
    PoiSwimmingPlaces,
    PoiIceRink,
    FacebookIcon, 
    InstagramIcon, 
    LinkedInIcon, 
    TripAdvisorIcon, 
    TwitterIcon, 
    VimeoIcon, 
    YouTubeIcon, 
    InformationSeverityInfo,
    InformationSeverityWarning,
    InformationSeverityImportant
} from '../assets';
import { TFunction } from 'i18next'; 

const FRESHLY_GROOMED_CUTOFF = 1;

enum GroomedState {
    Grooming = 'grooming',
    FreshlyGroomed = 'freshlyGroomed',
    GroomedToday = 'groomedToday',
    GroomedYesterday = 'groomedYesterday',
    GroomedWithinWeek = 'groomedWithinWeek',
    GroomedPastWeek = 'groomedPastWeek',
};
  
type GroomedStateReturnType = {
    state: string;
    hoursPassed?: number;
    weekday?: number;
};

type SocialMediaIcons = {
    [key: string]: JSX.Element;
  };

const formatLength = (length: number): string => `${length.toFixed(1).replace('.', ',')} km`;

const filterDisplayed = <T,>(arrayOrResult: T[] | { rows: T[]; count: number } | undefined | null): T[] => {
    if (!arrayOrResult) return [];
    const date = new Date();
    const array = Array.isArray(arrayOrResult) ? arrayOrResult : arrayOrResult.rows;
    if (!Array.isArray(array)) return [];
    const filtered: T[] = array.filter((entry: any) => {
        return entry.display == DisplayStatus.Display || 
             ( entry.display == DisplayStatus.Scheduled &&
               entry.timePeriod &&
               new Date(entry.timePeriod?.start) <= date &&
               new Date(entry.timePeriod?.end) >= date);
    });
    return filtered;
};

const getDifficultyIcon = (difficulty: string, props?: any) => {
    switch (difficulty) {
        case PathDifficulty.VeryEasy:
            return <DifficultyVeryEasy {...props} />;

        case PathDifficulty.Easy:
            return <DifficultyEasy {...props} />;

        case PathDifficulty.Medium:
            return <DifficultyMedium {...props} />;

        case PathDifficulty.Hard:
            return <DifficultyHard {...props} />;

        default:
            return <View {...props}></View>;
    }
};

const getDifficultyName = (t: TFunction, difficulty: string, plural: boolean): string => {
    switch (difficulty) {
        case PathDifficulty.VeryEasy:
            return plural ? t('veryEasyPlural') : t('veryEasy');

        case PathDifficulty.Easy:
            return plural ? t('easyPlural') : t('easy');

        case PathDifficulty.Medium:
            return plural ? t('mediumPlural') : t('medium');

        case PathDifficulty.Hard:
            return plural ? t('hardPlural') : t('hard');

        default:
            return '';
    }
};

const getGroomedState = (groomedAt: string): GroomedStateReturnType => {
    const now = moment();
    const date = moment(groomedAt);

    const timePassed = moment.duration(now.diff(date));
    const hoursPassed = Math.floor(timePassed.asHours());
    const weekday = date.day();

    if (hoursPassed < FRESHLY_GROOMED_CUTOFF) {
        return {state: GroomedState.Grooming};
    }

    if (date.isSame(now.clone().startOf('day'), 'd')) {
        return {state: GroomedState.GroomedToday, hoursPassed, weekday};
    }

    if (date.isSame(now.clone().subtract(1, 'days').startOf('day'), 'd')) {
        return {state: GroomedState.GroomedYesterday, hoursPassed, weekday};
    }
    return {state: GroomedState.GroomedPastWeek, hoursPassed, weekday};
};

const getStatusText = (t: TFunction, status: string, groomedAt?: string | null): string => {
    switch (status) {
        case PathStatus.Open:
            if (groomedAt) {
                const groomedState = getGroomedState(groomedAt);

                const weekdays = [
                    t('abbreviatedSunday'),
                    t('abbreviatedMonday'),
                    t('abbreviatedTuesday'),
                    t('abbreviatedWednesday'),
                    t('abbreviatedThursday'),
                    t('abbreviatedFriday'),
                    t('abbreviatedSaturday'),
                ];

                switch (groomedState.state) {
                    case GroomedState.Grooming:
                        return t('groomedStatusCurrently');

                    case GroomedState.FreshlyGroomed:
                    case GroomedState.GroomedToday:
                        return t('groomedStatusPassed', {hoursPassed: groomedState.hoursPassed});

                    case GroomedState.GroomedYesterday:
                        return t('groomedStatusYesterday');

                    case GroomedState.GroomedWithinWeek:
                        return t('groomedStatusWeekday', {weekday: weekdays[groomedState.weekday || 0]});

                    default: 
                        return t('open');
                }
            }
            return t('open');

        case PathStatus.Closed:
            return t('closed');
        
        case PathStatus.NotSet:
            return '---';

        default:
            return '';
    }
};

const getStatusColor = (status: string, groomedAt?: string | null): string => {
    switch (status) {
        case PathStatus.Open:
            if (groomedAt) {
            const groomedState = getGroomedState(groomedAt);

            if (
                groomedState.state === GroomedState.Grooming ||
                groomedState.state === GroomedState.FreshlyGroomed ||
                groomedState.state === GroomedState.GroomedToday
            ) {
                return colors.neutralColor400;
            }
            }
            return colors.successColor500;

        case PathStatus.Closed:
            return colors.errorColor500;

        default:
            return colors.graytone5;
    }
};

type CategoryRef = string | Partial<{ id: string | number; iconKey?: string | null }> | null | undefined;

const categoryIdOf = (category: CategoryRef): string =>
    typeof category === 'string' ? category : `${category?.id ?? ''}`;

/** Icon chosen by a super admin in admin (shared icon library), if any. */
const getLibraryCategoryIcon = (category: CategoryRef, props?: any) =>
    typeof category === 'object' && category && hasIcon(category.iconKey)
        ? <LibraryIcon {...props} iconKey={category.iconKey} />
        : undefined;

const getPathCategoryIcon = (pathCategory: CategoryRef, props?: any) => {
    const libraryIcon = getLibraryCategoryIcon(pathCategory, props);
    if (libraryIcon) {
        return libraryIcon;
    }
    switch (categoryIdOf(pathCategory)) {
        case PathCategoryId.CrossCountrySkiing:
            return <PathCrossCountrySkiing {...props}/>;

        case PathCategoryId.Snowmobiling:
            return <PathSnowmobiling {...props}/>;

        case PathCategoryId.MountainBiking:
            return <PathMountainBiking {...props}/>;

        case PathCategoryId.Hiking:
            return <PathHiking {...props}/>;
        
        case PathCategoryId.Running:
            return <PathRunning {...props}/>;
        
        case PathCategoryId.Riding:
            return <PathRiding {...props}/>;
        
        case PathCategoryId.Canoe:
            return <PathCanoe {...props}/>;
        
        case PathCategoryId.ChairLift: 
            return <PathChairLift {...props}/>;
        
        case PathCategoryId.Gondola:
            return <PathGondola {...props}/>;
        
        case PathCategoryId.SurfaceLift:
            return <PathSurfaceLift {...props}/>;
        
        case PathCategoryId.BeltLift:
            return <PathBeltLift {...props}/>;

        case PathCategoryId.SkiSlope:
            return <PathSkiSlope {...props}/>;

        default:
            return <View {...props}></View>;
    }
};

const getPoiCategoryIcon = (poiCategory: CategoryRef, props?: any) => {
    const libraryIcon = getLibraryCategoryIcon(poiCategory, props);
    if (libraryIcon) {
        return libraryIcon;
    }
    switch (categoryIdOf(poiCategory)) {
        case PoiCategoryId.Restaurants:
            return <PoiRestaurants {...props}/>;

        case PoiCategoryId.Cafes:
            return <PoiCafes {...props} />;

        case PoiCategoryId.Attractions:
            return <PoiPlaceOfInterests {...props}/>;

        case PoiCategoryId.Viewpoints:
            return <PoiViewpoints {...props}/>;

        case PoiCategoryId.Parkings:
            return <PoiParking {...props}/>;

        case PoiCategoryId.Cabins:
        case PoiCategoryId.RastsHouses:
            return <PoiCabins {...props}/>;
        
        case PoiCategoryId.BusStops:
            return <PoiBusStops {...props}/>;

        case PoiCategoryId.Shelters:
            return <PoiShelters {...props}/>;

        case PoiCategoryId.Shops:
        case PoiCategoryId.ScooterRentals:
        case PoiCategoryId.SportsRentals:
            return <PoiStores {...props}/>;
    
        case PoiCategoryId.BicycleWashes:
        case PoiCategoryId.Vallabodars:
            return <PoiAttractions {...props}/>;
        
        case PoiCategoryId.Toilets:
            return <PoiToilets {...props}/>;
        
        case PoiCategoryId.Warnings:
            return <PoiWarnings {...props}/>;

        case PoiCategoryId.SwimmingPlaces:
            return <PoiSwimmingPlaces {...props}/>;

        case PoiCategoryId.IceRink:
            return <PoiIceRink {...props}/>;

        default:
            return <View {...props}></View>;
    }
};

const getAoiCategoryIcon = (aoiCategory: CategoryRef, props?: any) => {
    const libraryIcon = getLibraryCategoryIcon(aoiCategory, props);
    if (libraryIcon) {
        return libraryIcon;
    }
    switch (categoryIdOf(aoiCategory)) {
        case AoiCategoryId.WarningArea:
            return <AoiWarning {...props}/>;

        case AoiCategoryId.ImportantArea:
            return <AoiImportant {...props}/>;

        case AoiCategoryId.InfoArea:
            return <AoiInfo {...props}/>;

        default:
            return <View {...props}></View>;
    }
};

const getInformationSeverityIcon = (severity: InformationSeverity, props?: any) => {
    switch (severity) {
        case InformationSeverity.Info:
            return <InformationSeverityInfo {...props}/>;

        case InformationSeverity.Important:
            return <InformationSeverityImportant {...props}/>;

        case InformationSeverity.Warning:
            return <InformationSeverityWarning {...props}/>;

        default:
            return <View {...props}></View>;
    }
};

const getInformationSeverityColor = (severity: InformationSeverity): string => {
    switch (severity) {
        case InformationSeverity.Info:
            return '#8BAC98';
        
        case InformationSeverity.Important:
            return '#CE6B00';
        
        case InformationSeverity.Warning:
            return '#9E2A14';

        default:
            return '#8BAC98';
    }
};

const socialMediaIcons: SocialMediaIcons = {
    instagramUrl: <InstagramIcon />,
    facebookUrl: <FacebookIcon />,
    linkedinUrl: <LinkedInIcon />,
    twitterUrl: <TwitterIcon />,
    vimeoUrl: <VimeoIcon />,
    youtubeUrl: <YouTubeIcon />,
    tripadvisorUrl: <TripAdvisorIcon />,
};
export {
    formatLength,
    filterDisplayed,
    getDifficultyIcon,
    getDifficultyName,
    getStatusText,
    getStatusColor,
    getPathCategoryIcon,
    getPoiCategoryIcon,
    socialMediaIcons,
    getAoiCategoryIcon,
    getInformationSeverityIcon,
    getInformationSeverityColor,
};